import React, {useMemo} from 'react';
import ReactMarkdown, {Components} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Link} from 'react-router-dom';
import CodeBlock from '../../components/utility/markdown/CodeBlock';
import isAdaptiveCard from '../../lib/isAdaptiveCard';
import {
    ARTIFACT_PLACEHOLDER,
    artifactPathFromHref,
    rewriteSandboxLinks
} from '../artifacts';
import AdaptiveCardViewer from './AdaptiveCardViewer';
import {
    ArtifactDownloadLink,
    ArtifactImage,
    useArtifactMap
} from './ArtifactViews';

// A link is "in-site" if it's relative (/...) or absolute with the same origin
// as the running app; those navigate client-side via react-router instead of
// forcing a full page load.
const toInternalPath = (href: string): string | null => {
    if (href.startsWith('/')) {
        return href;
    }

    try {
        const url = new URL(href);
        if (url.origin === window.location.origin) {
            return url.pathname + url.search + url.hash;
        }
    } catch {
        // Not an absolute URL (e.g. mailto:, #anchor) - treat as external.
    }

    return null;
};

const placeholderText: Record<string, string> = {
    [ARTIFACT_PLACEHOLDER.processing]: 'file is being prepared',
    [ARTIFACT_PLACEHOLDER.error]: 'file could not be saved',
    [ARTIFACT_PLACEHOLDER.missing]: 'file not available'
};

const markdownComponents: Partial<Components> = {
    a: ({href, children}) => {
        // A sandbox: link that could not be resolved to a real file
        if (href && placeholderText[href]) {
            return (
                <span className="text-muted" title={placeholderText[href]}>
                    {children} ({placeholderText[href]})
                </span>
            );
        }

        // Generated files need the bearer token, so they are not plain anchors.
        // Checked before the in-site rule: the API can share the app's origin.
        const artifactPath = artifactPathFromHref(href);
        if (artifactPath) {
            return (
                <ArtifactDownloadLink downloadPath={artifactPath}>
                    {children}
                </ArtifactDownloadLink>
            );
        }

        const internalPath = href ? toInternalPath(href) : null;

        if (internalPath) {
            return <Link to={internalPath}>{children}</Link>;
        }

        return (
            <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        );
    },
    img: ({src, alt}) => {
        if (src && placeholderText[src]) {
            return (
                <span className="text-muted">
                    {alt || 'image'} ({placeholderText[src]})
                </span>
            );
        }

        const artifactPath = artifactPathFromHref(src);
        if (artifactPath) {
            return <ArtifactImage downloadPath={artifactPath} alt={alt} />;
        }

        return <img src={src} alt={alt ?? ''} className="img-fluid" />;
    },
    code: ({className, children, ...props}) => {
        const language = className ? className.replace('language-', '') : '';

        if (language === 'json') {
            try {
                const parsedJson = JSON.parse(String(children).trim());
                if (isAdaptiveCard(parsedJson)) {
                    return <AdaptiveCardViewer payload={parsedJson} />;
                }
            } catch {
                // Not valid JSON - render as a regular code block
            }
        }

        return (
            <CodeBlock
                className={className}
                startLinePosition={props.node?.position?.start.line}
                endLinePosition={props.node?.position?.end.line}
            >
                {children}
            </CodeBlock>
        );
    }
};

const MarkdownMessage: React.FC<{text: string; isStreaming?: boolean}> = ({
    text,
    isStreaming = false
}) => {
    const artifactMap = useArtifactMap();

    // Models write sandbox:/mnt/data/... links out of habit; resolve them to the
    // real files before rendering (react-markdown drops unknown URL schemes).
    const resolvedText = useMemo(
        () => rewriteSandboxLinks(text, artifactMap, isStreaming),
        [text, artifactMap, isStreaming]
    );

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
        >
            {resolvedText}
        </ReactMarkdown>
    );
};

export default MarkdownMessage;
